import { useState } from "react";
import { toast } from "sonner";
import { API } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ShareNetwork, DownloadSimple, LinkSimple, InstagramLogo, TiktokLogo } from "@phosphor-icons/react";

const FORMATS = [["story", "Story · 9:16", "Instagram & TikTok stories"], ["square", "Square · 1:1", "Feed posts & DMs"]];

export default function ShareSheet({ slug, title, artist, compact = false }) {
  const [open, setOpen] = useState(false);
  const [fmt, setFmt] = useState("story");
  const [busy, setBusy] = useState(false);
  const url = `${window.location.origin}/presave/${slug}`;
  const img = `${API}/presave/${slug}/card.png?format=${fmt}`;
  const filename = `${title}-${artist}-${fmt}.png`.replace(/[^a-z0-9.-]+/gi, "-").toLowerCase();

  const blob = async () => { const res = await fetch(img); if (!res.ok) throw new Error(); return res.blob(); };

  const download = async () => {
    setBusy(true);
    try {
      const b = await blob();
      const href = URL.createObjectURL(b);
      const a = Object.assign(document.createElement("a"), { href, download: filename });
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(href);
      toast.success("Card downloaded — post it to your story and add the link sticker");
    } catch { toast.error("Could not build the share card"); }
    finally { setBusy(false); }
  };

  const copy = () => { navigator.clipboard.writeText(url); toast.success("Pre-save link copied"); };

  const native = async () => {
    setBusy(true);
    try {
      const file = new File([await blob()], filename, { type: "image/png" });
      const data = { files: [file], title: `${title} — ${artist}`, text: `Pre-save "${title}" by ${artist}: ${url}` };
      if (navigator.canShare?.(data)) await navigator.share(data);
      else if (navigator.share) await navigator.share({ title: data.title, text: data.text, url });
      else { await download(); }
    } catch (e) { if (e?.name !== "AbortError") toast.error("Sharing not available here — download instead"); }
    finally { setBusy(false); }
  };

  const canNative = typeof navigator !== "undefined" && !!navigator.share;

  return (
    <>
      <button onClick={() => setOpen(true)} data-testid={`share-open-${slug}`}
        className={compact
          ? "text-[10px] tracking-[0.15em] uppercase border border-zinc-700 px-2 py-1 hover:border-red-600 hover:text-red-500 transition-colors inline-flex items-center gap-1"
          : "w-full border border-zinc-700 py-3 text-xs tracking-[0.2em] uppercase inline-flex items-center justify-center gap-2 btn-brutal hover:border-white"}>
        <ShareNetwork size={compact ? 10 : 14} weight="bold" /> Share
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-black border-zinc-800 text-white max-w-3xl p-0 overflow-hidden" data-testid="share-sheet">
          <DialogHeader className="px-6 pt-6 pb-0">
            <DialogTitle className="font-display uppercase tracking-tight text-2xl">Share <span className="text-red-600">{title}</span></DialogTitle>
          </DialogHeader>
          <div className="grid md:grid-cols-12 gap-6 p-6">
            <div className="md:col-span-5 flex justify-center bg-zinc-950 border border-zinc-800 p-4">
              <img key={img} src={img} alt={`${title} share card`} data-testid="share-preview" className={`${fmt === "story" ? "h-[420px]" : "h-[300px]"} w-auto object-contain`} />
            </div>
            <div className="md:col-span-7 space-y-5">
              <div className="grid grid-cols-2 gap-2">
                {FORMATS.map(([id, l, sub]) => (
                  <button key={id} onClick={() => setFmt(id)} data-testid={`share-format-${id}`} className={`text-left border p-3 transition-colors ${fmt === id ? "border-red-600 bg-red-600/5" : "border-zinc-800 hover:border-zinc-600"}`}>
                    <div className="text-xs tracking-[0.15em] uppercase">{l}</div><div className="text-[10px] text-zinc-500 mt-0.5">{sub}</div>
                  </button>
                ))}
              </div>
              <div className="text-xs text-zinc-500 leading-relaxed flex items-start gap-2">
                <InstagramLogo size={14} className="shrink-0 mt-0.5" /><TiktokLogo size={14} className="shrink-0 mt-0.5" />
                <span>Post the card to your story, then add a <span className="text-white">Link sticker</span> pointing to your pre-save page. On your phone, <span className="text-white">Share</span> opens Instagram/TikTok directly.</span>
              </div>
              <div className="flex flex-col gap-2">
                {canNative && <button onClick={native} disabled={busy} data-testid="share-native" className="bg-red-600 text-white py-3 text-xs tracking-[0.2em] uppercase inline-flex items-center justify-center gap-2 btn-brutal btn-brutal-red disabled:opacity-50"><ShareNetwork size={14} weight="bold" /> Share to Instagram / TikTok</button>}
                <button onClick={download} disabled={busy} data-testid="share-download" className={`${canNative ? "border border-zinc-700 hover:border-white" : "bg-red-600 text-white btn-brutal-red"} py-3 text-xs tracking-[0.2em] uppercase inline-flex items-center justify-center gap-2 btn-brutal disabled:opacity-50`}><DownloadSimple size={14} weight="bold" /> {busy ? "Building…" : "Download image"}</button>
                <button onClick={copy} data-testid="share-copy-link" className="border border-zinc-700 py-3 text-xs tracking-[0.2em] uppercase inline-flex items-center justify-center gap-2 btn-brutal hover:border-white"><LinkSimple size={14} weight="bold" /> Copy pre-save link</button>
              </div>
              <div className="font-mono text-[10px] text-zinc-500 break-all" data-testid="share-url">{url}</div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
